import { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Save,
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Minus,
  Link2,
  Unlink,
  RefreshCw,
  Settings,
  Loader2,
  ArrowRight,
  Check,
  X,
  Sparkles,
  FileText,
  GitCompare,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function ActualsPage() {
  const { budgetId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeader } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [budget, setBudget] = useState(null);
  const [property, setProperty] = useState(null);
  const [varianceData, setVarianceData] = useState(null);
  const [actuals, setActuals] = useState([]);
  
  // Accounting integration state
  const [providers, setProviders] = useState([]);
  const [connections, setConnections] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [glMappings, setGlMappings] = useState([]);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [autoMapResult, setAutoMapResult] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [comparisonPeriod, setComparisonPeriod] = useState("ytd");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
    fetchProviders();
    fetchConnections();
    
    // Check if just connected
    const connected = searchParams.get("connected");
    if (connected) {
      toast.success(`Successfully connected to ${connected.toUpperCase()}!`);
      // Remove query param
      navigate(`/actuals/${budgetId}`, { replace: true });
    }
  }, [budgetId]); // Fetch when budgetId changes

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (connections.length > 0) {
      fetchGLMappings();
      fetchComparison();
    }
  }, [connections, comparisonPeriod]); // Fetch when connections or period changes

  const fetchData = async () => {
    try {
      const budgetRes = await axios.get(`${API}/budgets/${budgetId}`, {
        headers: getAuthHeader(),
      });
      setBudget(budgetRes.data);
      
      const propertyRes = await axios.get(`${API}/properties/${budgetRes.data.property_id}`, {
        headers: getAuthHeader(),
      });
      setProperty(propertyRes.data);
      
      const varianceRes = await axios.get(`${API}/budgets/${budgetId}/variance`, {
        headers: getAuthHeader(),
      });
      setVarianceData(varianceRes.data);
      
      const initialActuals = varianceRes.data.monthly_variance.map((mv) => ({
        month: mv.month,
        year: varianceRes.data.fiscal_year,
        rooms_sold: mv.actual?.rooms_sold || 0,
        accommodation_revenue: mv.actual?.revenue || 0,
        other_income: mv.actual?.other_income || 0,
        total_expenses: mv.actual?.expenses || 0,
        notes: mv.actual?.notes || "",
        has_data: mv.has_actual,
      }));
      setActuals(initialActuals);
    } catch (error) {
      toast.error("Failed to load data");
      // Error handled
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const res = await axios.get(`${API}/accounting/providers`, {
        headers: getAuthHeader(),
      });
      setProviders(res.data.providers);
    } catch (error) {
      console.error("Failed to fetch providers:", error);
    }
  };

  const fetchConnections = async () => {
    try {
      const res = await axios.get(`${API}/accounting/connections/${budgetId}`, {
        headers: getAuthHeader(),
      });
      setConnections(res.data.connections);
    } catch (error) {
      console.error("Failed to fetch connections:", error);
    }
  };

  const fetchGLMappings = async () => {
    try {
      const res = await axios.get(`${API}/accounting/${budgetId}/gl-mappings`, {
        headers: getAuthHeader(),
      });
      setGlMappings(res.data.mappings);
    } catch (error) {
      // Failed to fetch GL mappings:", error);
    }
  };

  const fetchComparison = async () => {
    if (connections.length === 0) return;
    
    setLoadingComparison(true);
    try {
      const res = await axios.get(`${API}/accounting/${budgetId}/comparison`, {
        headers: getAuthHeader(),
        params: { period: comparisonPeriod }
      });
      setComparison(res.data);
    } catch (error) {
      // Failed to fetch comparison:", error);
    } finally {
      setLoadingComparison(false);
    }
  };

  const handleConnect = async (providerId) => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/accounting/connect/${providerId}`, {
        headers: getAuthHeader(),
        params: { budget_id: budgetId }
      });
      
      // Redirect to OAuth
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate connection");
      setConnecting(false);
    }
  };

  const handleDisconnect = async (connectionId) => {
    try {
      await axios.delete(`${API}/accounting/connections/${connectionId}`, {
        headers: getAuthHeader(),
      });
      toast.success("Disconnected successfully");
      fetchConnections();
      setComparison(null);
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const handleFetchAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const res = await axios.get(`${API}/accounting/${budgetId}/chart-of-accounts`, {
        headers: getAuthHeader(),
      });
      setChartOfAccounts(res.data.accounts);
      setShowMappingDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch accounts");
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleAutoMap = async () => {
    try {
      const res = await axios.post(`${API}/accounting/${budgetId}/auto-map`, {}, {
        headers: getAuthHeader(),
      });
      setAutoMapResult(res.data);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Auto-mapping failed");
    }
  };

  const handleSaveMappings = async () => {
    if (!autoMapResult) return;
    
    try {
      await axios.post(`${API}/accounting/${budgetId}/gl-mappings`, {
        mappings: autoMapResult.suggested_mappings.map(m => ({
          external_code: m.external_code,
          external_name: m.external_name,
          mapped_category: m.mapped_category,
          mapped_line_item: m.mapped_line_item,
        }))
      }, {
        headers: getAuthHeader(),
      });
      
      toast.success("Mappings saved!");
      setShowMappingDialog(false);
      fetchGLMappings();
      fetchComparison();
    } catch (error) {
      toast.error("Failed to save mappings");
    }
  };

  const handleActualChange = (index, field, value) => {
    const updated = [...actuals];
    updated[index] = {
      ...updated[index],
      [field]: field === "notes" ? value : parseFloat(value) || 0,
      has_data: true,
    };
    setActuals(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        budget_id: budgetId,
        monthly_actuals: actuals.filter(a => a.has_data).map(a => ({
          month: a.month,
          year: a.year,
          rooms_sold: a.rooms_sold,
          accommodation_revenue: a.accommodation_revenue,
          other_income: a.other_income,
          total_expenses: a.total_expenses,
          notes: a.notes,
        })),
      };
      
      await axios.post(`${API}/actuals`, payload, {
        headers: getAuthHeader(),
      });
      
      toast.success("Actuals saved successfully");
      
      const varianceRes = await axios.get(`${API}/budgets/${budgetId}/variance`, {
        headers: getAuthHeader(),
      });
      setVarianceData(varianceRes.data);
    } catch (error) {
      toast.error("Failed to save actuals");
      // Error handled
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatVariance = (value, isExpense = false) => {
    if (value === null || value === undefined) return "-";
    const isPositive = isExpense ? value < 0 : value > 0;
    return (
      <span className={isPositive ? "text-emerald-600" : value === 0 ? "text-slate-500" : "text-rose-600"}>
        {value > 0 ? "+" : ""}{formatCurrency(value)}
      </span>
    );
  };

  const getVarianceIcon = (pct, isExpense = false) => {
    if (pct === null || pct === undefined) return <Minus className="w-4 h-4 text-slate-300" />;
    const isGood = isExpense ? pct < 0 : pct > 0;
    if (Math.abs(pct) < 2) return <CheckCircle className="w-4 h-4 text-slate-400" />;
    if (isGood) return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    return <TrendingDown className="w-4 h-4 text-rose-500" />;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full spinner"></div>
            <p className="text-slate-600">Loading actuals...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const activeConnection = connections.find(c => c.status === "connected");

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate(`/reports/${budgetId}`)}
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Reports
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Actuals vs Budget
            </h1>
            <p className="text-slate-500 mt-1">
              {property?.name} • FY {varianceData?.fiscal_year}
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Actuals
          </Button>
        </div>

        {/* Accounting Connection Card */}
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Link2 className="w-5 h-5 text-blue-600" />
              Connect Accounting Software
            </CardTitle>
            <CardDescription>
              Import actual P&L data automatically from your accounting system
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activeConnection ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Check className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-emerald-800">
                        Connected to {activeConnection.provider.toUpperCase()}
                      </p>
                      <p className="text-sm text-emerald-600">
                        Connected {new Date(activeConnection.connected_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleFetchAccounts}
                      disabled={loadingAccounts}
                      className="border-blue-300 text-blue-700"
                    >
                      {loadingAccounts ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Settings className="w-4 h-4 mr-1" />
                      )}
                      Map GL Codes
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDisconnect(activeConnection.id)}
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                    >
                      <Unlink className="w-4 h-4 mr-1" />
                      Disconnect
                    </Button>
                  </div>
                </div>

                {/* GL Mappings Status */}
                {glMappings.length > 0 ? (
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="text-sm text-emerald-800">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      {glMappings.length} GL codes mapped to budget categories
                    </p>
                  </div>
                ) : (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      No GL codes mapped yet. Click "Map GL Codes" to set up mappings.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {providers.map((provider) => (
                  <div
                    key={provider.id}
                    className={`p-4 rounded-lg border-2 ${
                      provider.available
                        ? "border-slate-200 bg-white hover:border-blue-300 cursor-pointer"
                        : "border-slate-100 bg-slate-50 opacity-60"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{provider.name}</h3>
                        <p className="text-sm text-slate-600 mt-1">{provider.description}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {provider.scopes.map((scope) => (
                            <Badge key={scope} variant="secondary" className="text-xs">
                              {scope}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    <Button
                      className="w-full mt-4"
                      disabled={!provider.available || connecting}
                      onClick={() => handleConnect(provider.id)}
                    >
                      {connecting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ExternalLink className="w-4 h-4 mr-2" />
                      )}
                      {provider.available ? `Connect ${provider.name}` : "Not Configured"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Budget vs Actuals Comparison (from accounting software) */}
        {activeConnection && comparison?.connected && comparison?.mapped && (
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <GitCompare className="w-5 h-5 text-[#4B5E40]" />
                    Budget vs Actuals (from {activeConnection.provider.toUpperCase()})
                  </CardTitle>
                  <CardDescription>
                    Comparison using imported actual data
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={comparisonPeriod} onValueChange={setComparisonPeriod}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ytd">Year to Date</SelectItem>
                      <SelectItem value="monthly">This Month</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchComparison}
                    disabled={loadingComparison}
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingComparison ? "animate-spin" : ""}`} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingComparison ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : comparison?.comparison ? (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Card className="bg-slate-50">
                      <CardContent className="p-4">
                        <p className="text-sm text-slate-600">Total Budget</p>
                        <p className="text-xl font-bold">{formatCurrency(comparison.summary.total_budget)}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-50">
                      <CardContent className="p-4">
                        <p className="text-sm text-slate-600">Total Actual</p>
                        <p className="text-xl font-bold">{formatCurrency(comparison.summary.total_actual)}</p>
                      </CardContent>
                    </Card>
                    <Card className={comparison.summary.total_variance >= 0 ? "bg-emerald-50" : "bg-rose-50"}>
                      <CardContent className="p-4">
                        <p className="text-sm text-slate-600">Variance</p>
                        <p className={`text-xl font-bold ${comparison.summary.total_variance >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                          {formatCurrency(comparison.summary.total_variance)}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Detailed Comparison Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left p-3">Category</th>
                          <th className="text-right p-3">Budget</th>
                          <th className="text-right p-3">Actual</th>
                          <th className="text-right p-3">Variance</th>
                          <th className="text-right p-3">%</th>
                          <th className="text-center p-3">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparison.comparison.map((row, i) => (
                          <tr key={i} className="border-b hover:bg-slate-50">
                            <td className="p-3 font-medium">{row.category}</td>
                            <td className="text-right p-3">{formatCurrency(row.budget)}</td>
                            <td className="text-right p-3">{formatCurrency(row.actual)}</td>
                            <td className={`text-right p-3 font-medium ${row.variance >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                              {row.variance >= 0 ? "+" : ""}{formatCurrency(row.variance)}
                            </td>
                            <td className={`text-right p-3 ${row.variance_percent >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                              {row.variance_percent.toFixed(1)}%
                            </td>
                            <td className="text-center p-3">
                              <Badge variant={row.status === "under" ? "success" : row.status === "over" ? "destructive" : "secondary"}>
                                {row.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : comparison?.message ? (
                <div className="text-center py-8 text-slate-500">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                  <p>{comparison.message}</p>
                </div>
              ) : null}
            </CardContent>
          </Card>
        )}

        {/* Manual Entry Tabs */}
        <Tabs defaultValue="entry" className="space-y-4">
          <TabsList>
            <TabsTrigger value="entry">Manual Entry</TabsTrigger>
            <TabsTrigger value="chart">Variance Chart</TabsTrigger>
          </TabsList>

          <TabsContent value="entry">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Actuals Entry</CardTitle>
                <CardDescription>
                  Enter actual figures for each month. Leave blank for months without data.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px] text-sm">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="text-left p-3">Month</th>
                          <th className="text-right p-3">Budget Revenue</th>
                          <th className="text-right p-3">Actual Revenue</th>
                          <th className="text-right p-3">Budget Expenses</th>
                          <th className="text-right p-3">Actual Expenses</th>
                          <th className="text-center p-3">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {varianceData?.monthly_variance.map((mv, index) => (
                          <tr key={mv.month} className="border-b">
                            <td className="p-3 font-medium">{MONTHS[mv.month - 1]}</td>
                            <td className="text-right p-3 text-slate-600">
                              {formatCurrency(mv.budgeted.revenue)}
                            </td>
                            <td className="p-3">
                              <Input
                                type="number"
                                value={actuals[index]?.accommodation_revenue || ""}
                                onChange={(e) => handleActualChange(index, "accommodation_revenue", e.target.value)}
                                className="w-28 text-right ml-auto"
                                placeholder="0"
                              />
                            </td>
                            <td className="text-right p-3 text-slate-600">
                              {formatCurrency(mv.budgeted.expenses)}
                            </td>
                            <td className="p-3">
                              <Input
                                type="number"
                                value={actuals[index]?.total_expenses || ""}
                                onChange={(e) => handleActualChange(index, "total_expenses", e.target.value)}
                                className="w-28 text-right ml-auto"
                                placeholder="0"
                              />
                            </td>
                            <td className="text-center p-3">
                              {mv.has_actual ? (
                                <Badge variant={mv.variance.profit > 0 ? "success" : "destructive"}>
                                  {mv.variance.profit > 0 ? "On Track" : "Behind"}
                                </Badge>
                              ) : (
                                <Badge variant="secondary">No Data</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="chart">
            <Card>
              <CardHeader>
                <CardTitle>Budget vs Actual Variance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={varianceData?.monthly_variance.map((mv) => ({
                        month: MONTHS[mv.month - 1],
                        budgetRevenue: mv.budgeted.revenue,
                        actualRevenue: mv.actual?.revenue || 0,
                        budgetExpenses: mv.budgeted.expenses,
                        actualExpenses: mv.actual?.expenses || 0,
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        labelStyle={{ color: "#1e293b" }}
                      />
                      <Legend />
                      <Bar dataKey="budgetRevenue" name="Budget Revenue" fill="#94a3b8" />
                      <Bar dataKey="actualRevenue" name="Actual Revenue" fill="#059669" />
                      <Bar dataKey="budgetExpenses" name="Budget Expenses" fill="#cbd5e1" />
                      <Bar dataKey="actualExpenses" name="Actual Expenses" fill="#e11d48" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* GL Mapping Dialog */}
        <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Map GL Codes to Budget Categories</DialogTitle>
              <DialogDescription>
                Match your accounting GL codes to budget expense categories for accurate comparison
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Auto-map button */}
              <div className="flex justify-between items-center p-4 bg-emerald-50 rounded-lg">
                <div>
                  <p className="font-medium text-emerald-800">Auto-Map GL Codes</p>
                  <p className="text-sm text-emerald-600">
                    Automatically suggest mappings based on GL code names
                  </p>
                </div>
                <Button onClick={handleAutoMap} className="bg-emerald-600 hover:bg-emerald-700">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Auto-Map
                </Button>
              </div>

              {/* Auto-map results */}
              {autoMapResult && (
                <div className="space-y-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      {autoMapResult.suggested_mappings.length} accounts auto-mapped
                      {autoMapResult.unmapped_accounts.length > 0 && (
                        <span className="text-amber-700 ml-2">
                          • {autoMapResult.unmapped_accounts.length} need manual mapping
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Suggested Mappings */}
                  <div>
                    <h4 className="font-medium mb-2">Suggested Mappings</h4>
                    <ScrollArea className="h-[200px]">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left p-2">GL Code</th>
                            <th className="text-left p-2">GL Name</th>
                            <th className="text-left p-2">→</th>
                            <th className="text-left p-2">Budget Category</th>
                            <th className="text-right p-2">Confidence</th>
                          </tr>
                        </thead>
                        <tbody>
                          {autoMapResult.suggested_mappings.map((mapping, i) => (
                            <tr key={i} className="border-b">
                              <td className="p-2 font-mono text-xs">{mapping.external_code}</td>
                              <td className="p-2">{mapping.external_name}</td>
                              <td className="p-2 text-slate-400">→</td>
                              <td className="p-2">{mapping.mapped_category}</td>
                              <td className="p-2 text-right">
                                <Badge variant={mapping.confidence > 0.7 ? "success" : "secondary"}>
                                  {(mapping.confidence * 100).toFixed(0)}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </div>

                  {/* Unmapped Accounts */}
                  {autoMapResult.unmapped_accounts.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2 text-amber-700">Unmapped Accounts</h4>
                      <ScrollArea className="h-[150px]">
                        <div className="space-y-2">
                          {autoMapResult.unmapped_accounts.map((acc, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-amber-50 rounded">
                              <div>
                                <span className="font-mono text-xs mr-2">{acc.external_code}</span>
                                <span>{acc.external_name}</span>
                              </div>
                              <Badge variant="outline">{acc.class}</Badge>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                      <p className="text-xs text-amber-600 mt-2">
                        These accounts don't have matching budget categories. You can add them to your budget if needed.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Raw Chart of Accounts (if no auto-map yet) */}
              {!autoMapResult && chartOfAccounts.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Chart of Accounts ({chartOfAccounts.length} accounts)</h4>
                  <ScrollArea className="h-[300px]">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left p-2">Code</th>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Type</th>
                          <th className="text-left p-2">Class</th>
                        </tr>
                      </thead>
                      <tbody>
                        {chartOfAccounts.map((acc, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-2 font-mono text-xs">{acc.code}</td>
                            <td className="p-2">{acc.name}</td>
                            <td className="p-2">{acc.type}</td>
                            <td className="p-2">
                              <Badge variant="outline">{acc.class}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMappingDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveMappings}
                disabled={!autoMapResult}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Check className="w-4 h-4 mr-2" />
                Save Mappings
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
